import type { GroupAnsweringNotes } from "./group-answering-notes";

/** `name` is the representative(s); `many` = 2+ of them (English then says "who act"). */
type RepNote = (name: string, many: boolean) => string;

/** QL-660 — a POA/guardian acting for the group's representative member. */
type ViaMemberParams = { name: string; role: string; member: string };

export const T = {
  en_GB: {
    representative: {
      sustainability: (name: string, many: boolean) =>
        `Sustainability preferences are answered by ${name}, who ${many ? "act" : "acts"} on behalf of the group.`,
      riskAssessment: (name: string, many: boolean) =>
        `Risk questions are answered by ${name}, who ${many ? "act" : "acts"} on behalf of the group.`,
      goals: (name: string, many: boolean) =>
        `Goals belong to the group. They are set by ${name}, who ${many ? "act" : "acts"} on behalf of the group.`,
      clientPreferences: (name: string, many: boolean) =>
        `Preferences are selected by ${name}, who ${many ? "act" : "acts"} on behalf of the group.`,
    },
    // QL-660 — the representative member acts through their own POA/guardian.
    viaMember: {
      sustainability: (p: ViaMemberParams) =>
        `Sustainability preferences are answered by ${p.name} (${p.role}), on behalf of ${p.member}, who acts for the group.`,
      riskAssessment: (p: ViaMemberParams) =>
        `Risk questions are answered by ${p.name} (${p.role}), on behalf of ${p.member}, who acts for the group.`,
      goals: (p: ViaMemberParams) =>
        `Goals belong to the group. They are set by ${p.name} (${p.role}), on behalf of ${p.member}, who acts for the group.`,
      clientPreferences: (p: ViaMemberParams) =>
        `Preferences are selected by ${p.name} (${p.role}), on behalf of ${p.member}, who acts for the group.`,
    },
    members: {
      sustainability:
        "Answer with the preferences the members agree on together. If they can't agree, use the strictest preference among them.",
      riskAssessment:
        "Answer on behalf of all members together. Where they differ, answer as the most cautious member would.",
      goals: "Goals belong to the group. The group agrees on each goal together.",
      clientPreferences: "Select a preference only if all members want it.",
    },
    /** Used for the name when the representative has no name on record. */
    repGeneric: "the group's representative",
  },
  nb_NO: {
    representative: {
      sustainability: (name: string, _many: boolean) =>
        `Bærekraftspreferansene besvares av ${name}, som handler på vegne av gruppen.`,
      riskAssessment: (name: string, _many: boolean) =>
        `Risikospørsmålene besvares av ${name}, som handler på vegne av gruppen.`,
      goals: (name: string, _many: boolean) =>
        `Målene tilhører gruppen. De settes av ${name}, som handler på vegne av gruppen.`,
      clientPreferences: (name: string, _many: boolean) =>
        `Preferansene velges av ${name}, som handler på vegne av gruppen.`,
    },
    viaMember: {
      sustainability: (p: ViaMemberParams) =>
        `Bærekraftspreferansene besvares av ${p.name} (${p.role}), på vegne av ${p.member}, som handler for gruppen.`,
      riskAssessment: (p: ViaMemberParams) =>
        `Risikospørsmålene besvares av ${p.name} (${p.role}), på vegne av ${p.member}, som handler for gruppen.`,
      goals: (p: ViaMemberParams) =>
        `Målene tilhører gruppen. De settes av ${p.name} (${p.role}), på vegne av ${p.member}, som handler for gruppen.`,
      clientPreferences: (p: ViaMemberParams) =>
        `Preferansene velges av ${p.name} (${p.role}), på vegne av ${p.member}, som handler for gruppen.`,
    },
    members: {
      sustainability:
        "Svar med preferansene medlemmene er enige om sammen. Hvis de ikke blir enige, bruk den strengeste preferansen blant dem.",
      riskAssessment:
        "Svar på vegne av alle medlemmene samlet. Der de er uenige, svar slik det mest forsiktige medlemmet ville gjort.",
      goals: "Målene tilhører gruppen. Gruppen blir enig om hvert mål sammen.",
      clientPreferences: "Velg en preferanse bare hvis alle medlemmene ønsker den.",
    },
    repGeneric: "gruppens representant",
  },
} as const satisfies Record<
  "en_GB" | "nb_NO",
  {
    representative: Record<keyof GroupAnsweringNotes, RepNote>;
    viaMember: Record<keyof GroupAnsweringNotes, (p: ViaMemberParams) => string>;
    members: GroupAnsweringNotes;
    repGeneric: string;
  }
>;
